/* Conditional show/hide for form fields.
 *
 * Each field wrapper rendered by wps_pages/shared/_form_field carries
 * data-wps-field="<name>", and conditional fields also carry
 * data-wps-conditional='{"action":"show","field":"ndis_plan","operator":"equals","value":"Plan Managed"}'.
 *
 * The operator semantics below must stay in lockstep with
 * WpsPages::Form#condition_matches? — the browser decides what the visitor
 * sees, the model decides what the server keeps.
 *
 * Gotcha: a select/radio option set to "Other" reports the literal sentinel
 * value `__other__`, not the option label, so a rule targeting the "Other"
 * choice must be written against `__other__`.
 */

const VALUELESS = ['is_empty', 'is_not_empty', 'checked', 'unchecked'];

// The <label> text for a radio/checkbox input, or '' when there isn't one.
function labelTextFor(form, ctrl) {
  const label = ctrl.id
    ? form.querySelector(`label[for="${CSS.escape(ctrl.id)}"]`)
    : ctrl.closest('label');

  return label ? label.textContent : '';
}

// Current values of a field, as an array of trimmed lowercase strings
// ([] when nothing is entered/selected).
//
// Both the selected option's value AND its visible label are reported, because
// a rule may have been authored against either: the builder used to save the
// option's label, while an options_source field's control value is the record
// id. Mirrors WpsPages::Form#condition_values.
function valuesFor(form, name) {
  const controls = form.querySelectorAll(
    `[name="submission[${name}]"], [name="submission[${name}][]"]`
  );

  const values = [];
  controls.forEach(ctrl => {
    if (ctrl.tagName === 'SELECT') {
      Array.from(ctrl.selectedOptions).forEach(opt => {
        if (opt.value !== '') values.push(opt.value, opt.textContent);
      });
    } else if (ctrl.type === 'radio' || ctrl.type === 'checkbox') {
      if (ctrl.checked && ctrl.value !== '') values.push(ctrl.value, labelTextFor(form, ctrl));
    } else if (ctrl.value !== '') {
      values.push(ctrl.value);
    }
  });

  return values.map(v => v.toString().trim().toLowerCase()).filter(v => v !== '');
}

function matches(values, operator, expected) {
  const exp = (expected || '').toString().trim().toLowerCase();

  switch (operator) {
    case 'equals':       return values.includes(exp);
    case 'not_equals':   return !values.includes(exp);
    case 'contains':     return values.some(v => v.includes(exp));
    case 'is_empty':
    case 'unchecked':    return values.length === 0;
    case 'is_not_empty':
    case 'checked':      return values.length > 0;
    default:             return true;
  }
}

// In the embedded/signup layouts the wrapper's parent is a grid cell
// (md:col-span-*). Hiding only the inner div would leave an empty column, so
// hide the cell instead. Tailwind's `hidden` (display:none) beats col-span,
// which sets no display of its own.
function hideTarget(el) {
  const parent = el.parentElement;
  return (parent && /col-span/.test(parent.className || '')) ? parent : el;
}

function buildIndex(form) {
  const index = {};
  form.querySelectorAll('[data-wps-field]').forEach(el => {
    index[el.dataset.wpsField] = el;
  });
  return index;
}

function isVisible(form, name, index, memo, visiting) {
  if (name in memo) return memo[name];
  if (visiting.includes(name)) return true;   // cycle -> treat as visible

  const el = index[name];
  if (!el || !el.dataset.wpsConditional) {
    memo[name] = true;
    return true;
  }

  let cond;
  try {
    cond = JSON.parse(el.dataset.wpsConditional);
  } catch (e) {
    memo[name] = true;
    return true;
  }

  visiting.push(name);
  // A field whose controlling field is itself hidden is hidden too.
  const controllerVisible = isVisible(form, cond.field, index, memo, visiting);
  visiting.pop();

  let result;
  if (!controllerVisible) {
    result = false;
  } else {
    const expected = VALUELESS.includes(cond.operator) ? '' : cond.value;
    const hit = matches(valuesFor(form, cond.field), cond.operator, expected);
    result = (cond.action === 'hide') ? !hit : hit;
  }

  memo[name] = result;
  return result;
}

// Hiding also disables the contained controls, which removes them from HTML5
// constraint validation (an invisible `required` input otherwise blocks submit
// with an unfocusable error) and from the submitted payload. The marker
// attribute means server-rendered `disabled` inputs are never re-enabled.
function applyVisibility(el, visible) {
  hideTarget(el).classList.toggle('hidden', !visible);

  el.querySelectorAll('input, select, textarea').forEach(ctrl => {
    if (!visible) {
      if (!ctrl.disabled) {
        ctrl.dataset.wpsCondDisabled = '1';
        ctrl.disabled = true;
      }
    } else if (ctrl.dataset.wpsCondDisabled) {
      delete ctrl.dataset.wpsCondDisabled;
      ctrl.disabled = false;
    }
  });
}

export function evaluateForm(form) {
  if (!form || !form.querySelector('[data-wps-conditional]')) return;

  const index = buildIndex(form);
  const memo = {};

  form.querySelectorAll('[data-wps-conditional]').forEach(el => {
    applyVisibility(el, isVisible(form, el.dataset.wpsField, index, memo, []));
  });
}

// Global delegated listeners — attached once at module load so they work
// regardless of when forms are rendered (Turbo navigations, Alpine, etc.).
// Both `input` and `change` so typing into a text controller updates its
// dependents live rather than only on blur.
function onFormActivity(event) {
  const name = event.target && event.target.name;
  if (!name || name.indexOf('submission[') !== 0) return;

  const form = event.target.closest('form');
  if (form) evaluateForm(form);
}

document.addEventListener('input', onFormActivity);
document.addEventListener('change', onFormActivity);

// Initial evaluation (also covers the re-render after a validation error and
// the Turbo Frame swap in _embedded_form).
export function initFormConditional() {
  document.querySelectorAll('form').forEach(evaluateForm);
}
